<template lang="pug">
.questionnaires-detail-tab
  .mb-3
  .box-body.register-body(v-if="questionnaireAndAnswers")
    .sub-title 受付状態
    .text {{ getStatus(_questionnaire) }}
    .mb-4
    .sub-title 宛先
    .text {{ getTargetType(_questionnaire) }}
    .mb-4

    .sub-title メッセージ内容
    .text-label.ml-3 タイトル
    .text.ml-3 {{ _questionnaire.messageTitle }}
    .mb-2
    .text-label.ml-3 本文
    .text.ml-3 {{ _questionnaire.messageText }}
    .mb-4

    .sub-title アンケート内容
    .text-label.ml-3 タイトル
    .text.ml-3 {{ _questionnaire.title }}
    .mb-2
    .text-label.ml-3 説明文
    .text.ml-3 {{ _questionnaire.text }}
    .mb-2
    .text-label.ml-3 添付ファイル
    .ml-3(v-if="0 < _fileNames.length")
      .list-group.mb-2(v-for="fileName in _fileNames")
        .list-group-item.file-name {{ fileName }}
      button.btn.preview-btn(@click="onTempFilePreview") プレビュー
    .text.ml-3(v-else) 添付ファイル無し
    .mb-4

    .sub-title 回答率
    .response-rate.d-flex.flex-row.align-items-center
      span {{ getResponseRate(_questionnaire, _questionnaireAnswers) }}
      .d-flex.flex-row.justify-content-end.flex-fill
        button.btn.mr-2(@click="onResponseUsers" :class="{ 'disable': _isHidden }"
          :disabled="_isHidden") 回答者一覧（{{ getResponseCount(_questionnaireAnswers) }}）
        button.btn.mr-2(@click="onNonresponseUsers" :class="{ 'disable': _isHidden }"
          :disabled="_isHidden") 未回答者一覧（{{ getNonresponseCount(_questionnaire, _questionnaireAnswers) }}）
        button.btn(v-if="!_isHidden" @click="onResendQuestionnaire") 未回答者に再送
        button.btn(v-else @click="onResendQuestionnaireToAll") 全員に再送
    .mt-2.warning-message(v-if="_questionnaire.hidden")
      span 本アンケートは匿名アンケートです。
      span 回答者一覧等、ダウンロードできるcsvファイルには回答者の情報は表示されません。
    .mb-4

    .sub-title 回答結果
    .response-rate.d-flex.flex-row.align-items-center
      span CSVにて結果をダウンロードします。
      .d-flex.flex-row.align-items-center.justify-content-end.flex-fill
        button.btn(@click="onDownloadCsv(_questionnaire, _questionnaireAnswers, responsedUsers)") ダウンロード
    .mb-4

    .questions
      .question.mb-2(v-for="(question, index) in questionAnswerDetails" no-body :key="`question-` + question.id")
        .text-question.d-flex.flex-row.mb-2
          .d-flex.flex-column.flex-fill
            .title {{ question.id + 1 }}. {{ question.title }}
          .d-flex.flex-column.align-items-end
            .link-text(@click="onQuestionAnswerDetail(question)") 詳細情報

        //- グラフ表示
        .question-parent(v-if="question.graphType != 'None'" :id="`question-` + question.id + '-parent'")
          .question-graph(:id="`question-` + question.id")

        //- グラフ表示以外（現状はテキスト）
        .text-question.d-flex.flex-row.mt-2(v-else)
          .count-area.d-flex.flex-column.align-items-center
            .count-title 回答数
            .count {{ question.texts.length }}
          .text-area.d-flex.flex-column.align-items-start
            .text-title 最新の回答（10件）
            //- 最新10件を表示するので、末尾10件だけを切り取って表示させる
            .text-area.d-flex.flex-column.align-items-center
            .text(v-for="text in question.texts.slice(-10)") {{ text }}

  SendMessageReadFamiliesModal(v-model="showFamiliesModal" :families="modalTargetFamilies" :csvTitle="csvTitle" :csvTypeName="csvTypeName")
  SendMessageReadMembersModal(v-model="showOtherUserGroupMemberModal" :otherUsers="modalTargetOtherUserGroupMember"
    :csvTitle="csvTitle" :csvTypeName="csvTypeName")
  QuestionnaireFilePreviewModal(ref="filePreviewModal")
  QuestionAnswerDetailsModal(v-model="showAnswerDetailModal" :title="answerDetailModalTitle"
    :fields="answerDetailModalFields" :items="answerDetailModalItems")

  PageLoading(:isBusy="isFileBusy" :message="fileloadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Prop, Ref, Vue, Watch } from "vue-property-decorator";
import { ChartData } from "chart.js";
import { PcUrl } from "@/commons/constants/PcUrl";
import { userStore } from "@/store/store";
import Setting from "../../../../public/setting.json";

import OtherUserGroup from "@/commons/models/OtherUserGroup";
import QuestionnairesService, {
  QuestionnairesGetModel,
  TargetType,
  QuestionnaireAnswersGetModel,
  QuestionnaireTextItem,
  QuestionnaireSelectItem,
  QuestionnaireSelectItemOption,
  TemplateType,
  FileUrl
} from "@/commons/services/Questionnaires";
import FamiliesService, { FamilyGetModel } from "@/commons/services/Families";
import OtherUserGroupMembersService from "@/commons/services/OtherUserGroupMembers";
import S3UrlService from "@/commons/services/S3Url";

import PageLoading from "@/pc/basics/PageLoading.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";
import SendMessageReadFamiliesModal from "@/pc/containers/questionnaires/SendMessageReadFamiliesModal.vue";
import SendMessageReadMembersModal from "@/pc/containers/questionnaires/SendMessageReadMembersModal.vue";
import QuestionnaireFilePreviewModal from "@/pc/containers/questionnaires/QuestionnaireFilePreviewModal.vue";

import DoughnutChart from "@/pc/basics/QuestionnaireAnswerDoughnutChart.vue";
import BarChart from "@/pc/basics/QuestionnaireAnswerBarChart.vue";
import { QuestionAnswerDetailsTableField, QuestionAnswerDetailsTableItem } from "./QuestionAnswerDetailsTable.vue";
import QuestionAnswerDetailsModal from "./QuestionAnswerDetailsModal.vue";

/**
 * ### アンケート登録画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    FormLabel,
    SendMessageReadFamiliesModal,
    SendMessageReadMembersModal,
    QuestionnaireFilePreviewModal,
    DoughnutChart,
    BarChart,
    QuestionAnswerDetailsModal
  }
})
export default class QuestionnaireDetail extends Vue {
  /** アンケートサービス */
  @Inject("QuestionnairesService") questionnairesService!: QuestionnairesService;
  /** 家族ユーザーサービス */
  @Inject("FamiliesService") familiesService!: FamiliesService;
  /** 施設関係者グループメンバーサービス */
  @Inject("OtherUserGroupMembersService") otherUserGroupMembersService!: OtherUserGroupMembersService;
  /** S3Urlサービス */
  @Inject("S3UrlService") s3UrlService!: S3UrlService;

  @Ref()
  filePreviewModal!: QuestionnaireFilePreviewModal;

  /** 読込み中か否か */
  @Prop()
  isBusy!: boolean;
  /** アンケート */
  @Prop()
  questionnaireAndAnswers!: QuestionnaireAndAnswers;
  /** リクエストの種類(家族or施設関係者) */
  @Prop()
  requestType!: string;
  /** 回答のユーザー(家族or施設関係者) */
  @Prop()
  responsedUsers!: Array<FamilyGetModel> | Array<OtherUserGroup>;
  /** 未回答のユーザー(家族or施設関係者) */
  @Prop()
  nonresponsedUsers!: Array<FamilyGetModel> | Array<OtherUserGroup>;

  /** ファイルローディング表示可否 */
  isFileBusy: boolean = false;
  /** ファイルローディングメッセージ */
  fileloadingMessage: string = "";

  /** 添付ファイル */
  tempFiles: Array<File> | null = null;

  /** CSV タイトル */
  csvTitle: string = "";
  /** CSV 種別名 */
  csvTypeName: string = "";

  /** 画面表示用の質問と回答 */
  questionAnswerDetails: Array<any> = [];
  /** グラフインスタンス */
  graphInstances: Array<DoughnutChart> = [];

  /** 家族リストモーダルの表示可否 */
  showFamiliesModal: boolean = false;
  /** 家族リストモーダルで表示する家族 */
  modalTargetFamilies: Array<FamilyGetModel> = [];
  /** 施設関係者リストモーダルの表示可否 */
  showOtherUserGroupMemberModal: boolean = false;
  /** 施設関係者リストモーダルで表示する施設関係者 */
  modalTargetOtherUserGroupMember: Array<OtherUserGroup> = [];

  /** 回答詳細モーダル表示可否 */
  showAnswerDetailModal: boolean = false;
  /** 回答詳細モーダルタイトル */
  answerDetailModalTitle: string = "";
  /** 回答詳細モーダル列 */
  answerDetailModalFields: Array<QuestionAnswerDetailsTableField> = [];
  /** 回答詳細モーダル行 */
  answerDetailModalItems: Array<QuestionAnswerDetailsTableItem> = [];

  get _questionnaire(): QuestionnairesGetModel | null {
    if (!this.questionnaireAndAnswers || !this.questionnaireAndAnswers.questionnaire) {
      return null;
    } else {
      return this.questionnaireAndAnswers.questionnaire;
    }
  }

  get _questionnaireAnswers(): Array<QuestionnaireAnswersGetModel> | null {
    if (!this.questionnaireAndAnswers || !this.questionnaireAndAnswers.questionnaireAnswers) {
      return null;
    } else {
      return this.questionnaireAndAnswers.questionnaireAnswers;
    }
  }

  get _isHidden(): boolean {
    if (!this.questionnaireAndAnswers || !this.questionnaireAndAnswers.questionnaire) {
      return false;
    }
    if (this.questionnaireAndAnswers.questionnaire.hidden) {
      return true;
    } else {
      return false;
    }
  }

  get _disableResend(): boolean {
    if (!this.questionnaireAndAnswers || !this.questionnaireAndAnswers.questionnaireAnswers) {
      return true;
    }
    if (this.questionnaireAndAnswers.questionnaire.targets.length - this.questionnaireAndAnswers.questionnaireAnswers.length <= 0) {
      return true;
    }
    if (this.questionnaireAndAnswers.questionnaire.acceptingAnswers == false) {
      return true;
    }
    return false;
  }

  get _fileNames(): string[] {
    const fileNames: string[] = [];

    // ファイルが存在しない場合があるので、その場合は処理終了
    if (!this.questionnaireAndAnswers.questionnaire.fileUrls) {
      return [];
    }

    if (0 < this.questionnaireAndAnswers.questionnaire.fileUrls.length) {
      for (const file of this.questionnaireAndAnswers.questionnaire.fileUrls) {
        fileNames.push(file.name);
      }
    }
    return fileNames;
  }

  /**
   * ### 受付状態文字列を返す
   */
  getStatus(questionnaire: QuestionnairesGetModel): string {
    if (questionnaire.acceptingAnswers) {
      // 期限が現在日時よりも過去の場合は受付終了として扱う
      if (this.isOverLimit(questionnaire)) {
        return "受付終了";
      } else {
        return "受付中";
      }
    } else {
      return "受付終了";
    }
  }

  /**
   * ### 期限が過ぎているかを返す
   *
   * - 期限が設定されていない場合は期限が過ぎていないものとして扱う
   */
  isOverLimit(questionnaire: QuestionnairesGetModel): boolean {
    if (questionnaire.replyLimit == null) {
      return false;
    }
    const now: number = this.$dateUtil.convertDateToNumber(new Date(), "yyyymmddhhMMss000");
    if (questionnaire.replyLimit < now) {
      return true;
    }
    return false;
  }

  /**
   * ### 宛先文字列を返す
   */
  getTargetType(questionnaire: QuestionnairesGetModel): string {
    switch (questionnaire.targetType) {
      case TargetType.ALL_FAMILIES: {
        return "家族全員";
      }
      case TargetType.FAMILIES: {
        return "家族個別";
      }
      case TargetType.OTHER_USERS: {
        return "施設関係者個別";
      }
      case TargetType.SEND_MESSAGE_GROUPS: {
        const groupNames: string[] = [];
        for (const groupInfo of questionnaire.sendMessageGroupInfos) {
          groupNames.push(groupInfo.name);
        }
        return groupNames.join("、");
      }
      case TargetType.RESIDENT_GROUPS: {
        const groupNames: string[] = [];
        for (const groupInfo of questionnaire.residentGroupInfos) {
          groupNames.push(groupInfo.name);
        }
        return groupNames.join("、");
      }
      case TargetType.OTHER_USER_GROUPS: {
        const groupNames: string[] = [];
        for (const groupInfo of questionnaire.otherUserGroupInfos) {
          groupNames.push(groupInfo.name);
        }
        return groupNames.join("、");
      }
      default: {
        return "";
      }
    }
  }

  /**
   * ### 添付ファイルプレビューボタン押下イベント処理
   *
   * - ダイアログで添付ファイルをプレビュー表示する
   */
  async onTempFilePreview(): Promise<void> {
    // アンケートが未取得の状態なら何もしない
    if (!this.questionnaireAndAnswers.questionnaire) {
      return;
    }
    // ファイルが未取得の場合は取得する
    if (!this.tempFiles) {
      this.isFileBusy = true;
      this.fileloadingMessage = "読込み中...";

      const tempFiles: Array<File> = this.questionnaireAndAnswers.questionnaire.fileUrls ? await this.getS3Files(this.questionnaireAndAnswers.questionnaire.fileUrls) : [];
      this.tempFiles = tempFiles;

      this.isFileBusy = false;
    }

    if (this.filePreviewModal && this.filePreviewModal instanceof QuestionnaireFilePreviewModal) {
      this.filePreviewModal.show("", false, this.tempFiles as File[]);
    }
    return;
  }

  /**
   * ### S3からファイルを取得する
   */
  private async getS3Files(fileUrls: Array<FileUrl>): Promise<Array<File>> {
    const files: Array<File> = [];
    for (const fileUrl of fileUrls) {
      const tempFile: File = await this.getS3File(fileUrl.url, fileUrl.name);
      files.push(tempFile);
    }
    return files;
  }

  /**
   * ### S3に格納されているFileを取得する
   *
   * @param url 元URL
   */
  private async getS3File(url: string, fileName: string): Promise<File> {
    const response: File = await this.s3UrlService.getFile(userStore, Setting.backets.imageExpireHour, url, fileName);
    return response;
  }

  /**
   * ### 回答率文字列を返す
   */
  getResponseRate(questionnaire: QuestionnairesGetModel, questionnaireAnswers: Array<QuestionnaireAnswersGetModel>): string {
    const responseRate: string = this.calculateResponseRate(questionnaire, questionnaireAnswers);
    const responseCount: number = questionnaireAnswers.length;
    const targetsCount: number = questionnaire.targets.length;
    return `${responseRate}％（${responseCount}／${targetsCount}人）`;
  }

  /**
   * ### 回答率(%)を計算する
   *
   * - 小数点以下切り捨て
   */
  private calculateResponseRate(questionnaire: QuestionnairesGetModel, questionnaireAnswers: Array<QuestionnaireAnswersGetModel>): string {
    const responseCount: number = questionnaireAnswers.length;
    const targetsCount: number = questionnaire.targets.length;
    const percentage: number = ((responseCount / targetsCount) * 10000) / 100;
    return percentage.toFixed(0);
  }

  /**
   * ### 回答者一覧ボタン押下イベント
   */
  onResponseUsers(): void {
    this.csvTitle = this.questionnaireAndAnswers.questionnaire.title;
    this.csvTypeName = "回答者一覧";

    if (this.requestType == "families") {
      this.modalTargetFamilies = this.responsedUsers as Array<FamilyGetModel>;
      this.showFamiliesModal = true;
    } else if (this.requestType == "otherUserGroups") {
      this.modalTargetOtherUserGroupMember = this.responsedUsers as Array<OtherUserGroup>;
      this.showOtherUserGroupMemberModal = true;
    }
    return;
  }

  /**
   * ### 回答者数を返す
   */
  getResponseCount(questionnaireAnswers: Array<QuestionnaireAnswersGetModel>): number {
    return questionnaireAnswers.length;
  }

  /**
   * ### 未回答者一覧ボタン押下イベント
   */
  onNonresponseUsers(): void {
    this.csvTitle = this.questionnaireAndAnswers.questionnaire.title;
    this.csvTypeName = "未回答者一覧";

    if (this.requestType == "families") {
      this.modalTargetFamilies = this.nonresponsedUsers as Array<FamilyGetModel>;
      this.showFamiliesModal = true;
    } else if (this.requestType == "otherUserGroups") {
      this.modalTargetOtherUserGroupMember = this.nonresponsedUsers as Array<OtherUserGroup>;
      this.showOtherUserGroupMemberModal = true;
    }
    return;
  }

  /**
   * ### 再送ボタン押下イベント
   */
  onResendQuestionnaire(): void {
    this.$emit("onResendQuestionnaire");
    return;
  }

  onResendQuestionnaireToAll(): void {
    this.$emit("onResendQuestionnaireToAll");
    return;
  }

  /**
   * ### 未回答者数を返す
   */
  getNonresponseCount(questionnaire: QuestionnairesGetModel, questionnaireAnswers: Array<QuestionnaireAnswersGetModel>): number {
    return questionnaire.targets.length - questionnaireAnswers.length;
  }

  /**
   * ### 回答結果をCSVダウンロードする
   */
  onDownloadCsv(
    questionnaire: QuestionnairesGetModel,
    questionnaireAnswers: Array<QuestionnaireAnswersGetModel>,
    responsedUsers: Array<FamilyGetModel> | Array<OtherUserGroup>
  ): void {
    const csvTitle: string = this.createCsvTitle(questionnaire);
    const csvData: string = this.createCsvLines(questionnaire, questionnaireAnswers, responsedUsers);
    const fileName: string = `HitomeQコネクト-${questionnaire.title}.csv`;
    this.downloadCsv(csvTitle + csvData, fileName);
    return;
  }

  /**
   * ### CSVの1行目(列名)を作成する
   *
   * - BOM付きで出力させるために`\ufeff`を先頭に付与
   * - 末尾に改行コードを付与する
   */
  private createCsvTitle(questionnaire: QuestionnairesGetModel): string {
    let labels: string[] = [];

    // 家族or施設関係者用の列名を格納する
    if (!questionnaire.hidden) {
      switch (this.requestType) {
        case "families": {
          const familyColumns: string[] = this.createFamilyColumns();
          labels = labels.concat(familyColumns);
          break;
        }
        case "otherUserGroups": {
          const memberColumns: string[] = this.createMemberColumns();
          labels = labels.concat(memberColumns);
          break;
        }
        default: {
          break;
        }
      }
    }

    // 回答日時を列名に追加する
    labels.push("回答日時");

    // 質問項目名を列名に追加する
    const questionsColumns: string[] = this.createQuestionsColumns(questionnaire);
    labels = labels.concat(questionsColumns);

    return "\ufeff" + labels.join(",") + "\n";
  }

  /** ### CSVの列名を作成する（家族） */
  private createFamilyColumns(): string[] {
    let columns: string[] = [];
    columns.push("部屋名");
    columns.push("利用者名");
    columns.push("家族ユーザー名");
    columns.push("役割");
    columns.push("電話番号");
    return columns;
  }

  /** ### CSVの列名を作成する（施設関係者） */
  private createMemberColumns(): string[] {
    let columns: string[] = [];
    columns.push("種類");
    columns.push("グループ名");
    columns.push("ユーザー名");
    columns.push("電話番号");
    columns.push("所属");
    return columns;
  }

  /** ### CSVの回答の部分を作成する */
  private createQuestionsColumns(questionnaire: QuestionnairesGetModel): string[] {
    const columns: string[] = [];
    for (const question of questionnaire.questions) {
      columns.push(question.title ? question.title : "");
    }
    return columns;
  }

  /**
   * ### CSV出力する行情報を作成する
   *
   * - 待機者1人で1行の情報とする
   *   - 行情報の末尾は\nで区切る
   * - 項目毎のanswer値を出力する
   *   - CSV出力するのでanswer値をカンマ区切りで出力する
   */
  private createCsvLines(
    questionnaire: QuestionnairesGetModel,
    questionnaireAnswers: Array<QuestionnaireAnswersGetModel>,
    responsedUsers: Array<FamilyGetModel> | Array<OtherUserGroup>
  ): string {
    let csvLines: string[] = []; // 全行情報

    switch (this.requestType) {
      case "families": {
        const families: Array<FamilyGetModel> = responsedUsers as Array<FamilyGetModel>;
        const map: Map<string, FamilyGetModel> = this.createFamilyMap(families);
        for (const answers of questionnaireAnswers) {
          const csvLine: string[] = this.createFamilyCsvLines(questionnaire, answers, map);
          csvLines.push(csvLine.join(","));
        }
        break;
      }
      case "otherUserGroups": {
        const members: Array<OtherUserGroup> = responsedUsers as Array<OtherUserGroup>;
        const map: Map<string, OtherUserGroup> = this.createMemberMap(members);
        for (const answers of questionnaireAnswers) {
          const csvLine: string[] = this.createMemberCsvLines(questionnaire, answers, map);
          csvLines.push(csvLine.join(","));
        }
        break;
      }
      default: {
        break;
      }
    }
    return csvLines.join("\n");
  }

  /**
   * ### 家族MAPを作成する
   *
   * - ソートキーをkeyとし、valueをレコードとする
   */
  private createFamilyMap(families: Array<FamilyGetModel>): Map<string, FamilyGetModel> {
    const map: Map<string, FamilyGetModel> = new Map<string, FamilyGetModel>();
    for (const family of families) {
      map.set(family.residentsSortKey, family);
    }
    return map;
  }

  /**
   * ### 施設関係者グループメンバーMAPを作成する
   *
   * - ソートキーをkeyとし、valueをレコードとする
   */
  private createMemberMap(members: Array<OtherUserGroup>): Map<string, OtherUserGroup> {
    const map: Map<string, OtherUserGroup> = new Map<string, OtherUserGroup>();
    for (const member of members) {
      map.set(member.otherUserGroupsSortKey, member);
    }
    return map;
  }

  /**
   * ### 家族用のCSV行を作成する
   */
  private createFamilyCsvLines(questionnaire: QuestionnairesGetModel, questionnaireAnswer: QuestionnaireAnswersGetModel, map: Map<string, FamilyGetModel>): string[] {
    let csvLine: string[] = [];

    // 家族の情報項目
    if (!questionnaire.hidden) {
      const family: FamilyGetModel | undefined = map.get(questionnaireAnswer.targetSortKey);
      if (family) {
        csvLine.push(family.room);
        csvLine.push(family.name);
        csvLine.push(family.familyName);
        csvLine.push(family.familyPosition ? family.familyPosition : "未設定");
        csvLine.push(`="${family.familyTel}"`);
      } else {
        // 家族が見つからない場合は家族情報がないので空文字で補完するしかない
        csvLine.push("");
        csvLine.push("");
        csvLine.push("");
        csvLine.push("");
        csvLine.push("");
      }
    }

    // 回答日時
    csvLine.push(this.$dateUtil.convertNumberToString(questionnaireAnswer.updated, "yyyy/mm/dd hh:MM"));

    // 質問事項の回答
    csvLine = csvLine.concat(this.createAnswerLines(questionnaireAnswer));

    return csvLine;
  }

  /**
   * ### 質問の回答行情報を作成する
   */
  private createAnswerLines(questionnaireAnswer: QuestionnaireAnswersGetModel): string[] {
    const csvLine: string[] = [];
    for (const answer of questionnaireAnswer.answers) {
      switch (answer.type) {
        case TemplateType.TEXT: {
          const textAnswer: QuestionnaireTextItem = answer as QuestionnaireTextItem;
          csvLine.push(`"${textAnswer.answer ? textAnswer.answer : ""}"`);
          break;
        }
        case TemplateType.SELECT: {
          const selectItem: QuestionnaireSelectItem = answer as QuestionnaireSelectItem;

          // LIFF側でnullを格納する場合があるのでその場合はスキップ
          if (selectItem.answer == null) {
            csvLine.push("");
            break;
          }

          const optionMap: Map<string, string> = this.createSelectOptionMap(selectItem);
          const line: string[] = [];
          for (const selectAnswer of selectItem.answer) {
            const answer: string | undefined = optionMap.get(selectAnswer);
            if (answer) {
              line.push(answer);
            }
          }
          csvLine.push(`"${line.join("\n")}"`);
          break;
        }
        default: {
          csvLine.push("");
          break;
        }
      }
    }
    return csvLine;
  }

  /**
   * ### 選択肢のMAPを作成する
   */
  private createSelectOptionMap(selectItem: QuestionnaireSelectItem): Map<string, string> {
    const optionMap: Map<string, string> = new Map<string, string>();
    for (const option of selectItem.options) {
      optionMap.set(option.value, option.text);
    }
    return optionMap;
  }

  /**
   * ### 施設関係者メンバー用のCSV行を作成する
   */
  private createMemberCsvLines(questionnaire: QuestionnairesGetModel, questionnaireAnswer: QuestionnaireAnswersGetModel, map: Map<string, OtherUserGroup>): string[] {
    let csvLine: string[] = [];

    // 施設関係者の情報項目
    if (!questionnaire.hidden) {
      const member: OtherUserGroup | undefined = map.get(questionnaireAnswer.targetSortKey);
      if (member) {
        csvLine.push(member.groupTypeName);
        csvLine.push(member.name);
        csvLine.push(member.memberName);
        csvLine.push(`="${member.memberTel}"`);
        csvLine.push(member.memberOrganization);
      } else {
        // 施設関係者が見つからない場合は施設関係者情報がないので空文字で補完するしかない
        csvLine.push("");
        csvLine.push("");
        csvLine.push("");
        csvLine.push("");
        csvLine.push("");
      }
    }

    // 回答日時
    csvLine.push(this.$dateUtil.convertNumberToString(questionnaireAnswer.updated, "yyyy/mm/dd hh:MM"));

    // 質問事項の回答
    csvLine = csvLine.concat(this.createAnswerLines(questionnaireAnswer));

    return csvLine;
  }

  /**
   * ### CSVファイルをダウンロードさせる
   *
   * - CSVの内容からblobファイルを作成する
   * - blobファイルの種別はtext/csv
   * - a要素を作ってそれを疑似的にクリックさせることでダウンロードさせる
   */
  private downloadCsv(csv: string, fileName: string): void {
    const blob: Blob = new Blob([csv], { type: "text/csv" });
    let link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    return;
  }

  @Watch("questionnaireAndAnswers")
  createGraphs(questionnaireAndAnswers: QuestionnaireAndAnswers): void {
    // 情報作成に必要なものが1つでも無ければ処理終了
    if (!questionnaireAndAnswers || !questionnaireAndAnswers.questionnaire || !questionnaireAndAnswers.questionnaireAnswers) {
      return;
    }

    const questionAnswerDetails: Array<QuestionAnswerDetail> = this.createQuestionAnswerDetails(questionnaireAndAnswers);
    this.questionAnswerDetails = questionAnswerDetails;

    // DoughnutChartはmounted前に子コンポーネントが存在していない場合は描画されない
    // しかし、質問数は不明のためDoughnutChartを何個表示するかが分からないため、情報取得してドキュメントの描画が終わったら動的にDoughnutChartを追加する
    this.$nextTick(() => {
      // 既存DoughnutChartインスタンスを削除する
      for (const instance of this.graphInstances) {
        instance.$destroy();
      }
      // インスタンスを削除したのでリストを初期化する
      this.graphInstances = [];

      for (const question of questionAnswerDetails) {
        const graphInstance: any = this.addGraph(question);
        this.graphInstances.push(graphInstance);
      }
    });
  }

  /** ### 回答を詳細表示する情報を作成する */
  private createQuestionAnswerDetails(questionnaireAndAnswers: QuestionnaireAndAnswers): Array<QuestionAnswerDetail> {
    const questionAnswerDetails: Array<QuestionAnswerDetail> = [];

    for (const [index, question] of questionnaireAndAnswers.questionnaire.questions.entries()) {
      // 作成するグラフは種別で分岐
      switch (question.type) {
        // 選択肢
        case TemplateType.SELECT: {
          const selectQuestion: QuestionnaireSelectItem = question as QuestionnaireSelectItem;
          // 単一ならドーナツチャート
          if (selectQuestion.multiple == false) {
            const chartjsData: ChartData = this.createSelectDoughnutChartData(selectQuestion, questionnaireAndAnswers.questionnaireAnswers, index);
            questionAnswerDetails.push({
              id: index,
              title: question.title,
              graphType: "DoughnutChart",
              displayRateGraph: Object.assign({}, chartjsData),
              rateGraph: chartjsData,
              items: []
            });
          }
          // 複数ならバーチャート
          else {
            const chartjsData: ChartData = this.createMultiSelectBarChartData(selectQuestion, questionnaireAndAnswers.questionnaireAnswers, index);
            questionAnswerDetails.push({
              id: index,
              title: question.title,
              graphType: "BarChart",
              displayRateGraph: Object.assign({}, chartjsData),
              rateGraph: chartjsData,
              items: []
            });
          }
          break;
        }
        // テキスト
        case TemplateType.TEXT: {
          const textQuestion: QuestionnaireTextItem = question as QuestionnaireTextItem;
          const texts: string[] = this.createTexts(textQuestion, questionnaireAndAnswers.questionnaireAnswers, index);
          questionAnswerDetails.push({
            id: index,
            title: question.title,
            texts: texts,
            graphType: "None",
            items: []
          });

          break;
        }
        default: {
          break;
        }
      }
    }

    return questionAnswerDetails;
  }

  /**
   * ### DoughnutChart用のグラフ情報を作成する
   */
  private createSelectDoughnutChartData(selectQuestion: QuestionnaireSelectItem, questionnaireAnswers: Array<QuestionnaireAnswersGetModel>, questionIndex: number): ChartData {
    const counts: number[] = [];
    const labels: string[] = [];
    const backgroundColors: string[] = [];
    const borderWidths: Array<number> = [];

    for (const [index, option] of selectQuestion.options.entries()) {
      // TODO options内の全部のtextを見て、最大文字数を調べた方が見栄えがいいが、一旦保留
      // labels.push(this.paddingGraphLabel(choice.label, maxLabelLength));
      labels.push(option.text);
      counts.push(this.getSelectAnswerCount(questionnaireAnswers, questionIndex, selectQuestion, index));
      backgroundColors.push(this.getGraphItemBgColor(index));
      borderWidths.push(0); // 0固定
    }
    const chartjsData: ChartData = {
      labels: labels,
      datasets: [
        {
          label: selectQuestion.title,
          backgroundColor: backgroundColors,
          borderWidth: borderWidths,
          data: counts
        }
      ]
    };
    return chartjsData;
  }

  /**
   * ### セレクト質問項目の回答数を返す
   */
  private getSelectAnswerCount(questionnaireAnswers: Array<QuestionnaireAnswersGetModel>, index: number, question: QuestionnaireSelectItem, optionIndex: number): number {
    let count: number = 0;
    const option: QuestionnaireSelectItemOption = question.options[optionIndex];
    for (const answer of questionnaireAnswers) {
      const question = answer.answers[index] as QuestionnaireSelectItem;

      // LIFF側でnullを格納する場合があるのでその場合はスキップ
      if (question.answer == null) {
        continue;
      }

      for (const selectAnswer of question.answer) {
        if (selectAnswer === option.value) {
          count++;
        }
      }
    }
    return count;
  }

  /** ### グラフの背景色を返す */
  private getGraphItemBgColor(index: number): string {
    const colors: string[] = [
      // 1～10
      "rgba(131, 205, 208, 1)",
      "rgba(230, 220, 105, 1)",
      "rgba(147, 186, 250, 1)",
      "rgba(161, 206, 151, 1)",
      "rgba(248, 192, 229, 1)",
      "rgba(255, 235, 209, 1)",
      "rgba(238, 233, 243, 1)",
      "rgba(245, 216, 218, 1)",
      "rgba(184, 184, 184, 1)",
      "rgba(188, 212, 245, 1)",
      // 11~20
      "rgba(131, 205, 208, 1)",
      "rgba(230, 220, 105, 1)",
      "rgba(147, 186, 250, 1)",
      "rgba(161, 206, 151, 1)",
      "rgba(248, 192, 229, 1)",
      "rgba(255, 235, 209, 1)",
      "rgba(238, 233, 243, 1)",
      "rgba(245, 216, 218, 1)",
      "rgba(184, 184, 184, 1)",
      "rgba(188, 212, 245, 1)",
      // 21~30
      "rgba(131, 205, 208, 1)",
      "rgba(230, 220, 105, 1)",
      "rgba(147, 186, 250, 1)",
      "rgba(161, 206, 151, 1)",
      "rgba(248, 192, 229, 1)",
      "rgba(255, 235, 209, 1)",
      "rgba(238, 233, 243, 1)",
      "rgba(245, 216, 218, 1)",
      "rgba(184, 184, 184, 1)",
      "rgba(188, 212, 245, 1)",
      // 31~40
      "rgba(131, 205, 208, 1)",
      "rgba(230, 220, 105, 1)",
      "rgba(147, 186, 250, 1)",
      "rgba(161, 206, 151, 1)",
      "rgba(248, 192, 229, 1)",
      "rgba(255, 235, 209, 1)",
      "rgba(238, 233, 243, 1)",
      "rgba(245, 216, 218, 1)",
      "rgba(184, 184, 184, 1)",
      "rgba(188, 212, 245, 1)",
      // 41~50
      "rgba(131, 205, 208, 1)",
      "rgba(230, 220, 105, 1)",
      "rgba(147, 186, 250, 1)",
      "rgba(161, 206, 151, 1)",
      "rgba(248, 192, 229, 1)",
      "rgba(255, 235, 209, 1)",
      "rgba(238, 233, 243, 1)",
      "rgba(245, 216, 218, 1)",
      "rgba(184, 184, 184, 1)",
      "rgba(188, 212, 245, 1)"
    ];
    return colors[index];
  }

  /**
   * ### BarChart用のグラフ情報を作成する
   */
  private createMultiSelectBarChartData(selectQuestion: QuestionnaireSelectItem, questionnaireAnswers: Array<QuestionnaireAnswersGetModel>, questionIndex: number): ChartData {
    // ラベル情報を作成する
    const labels = [];
    const data = [];
    const backgroundColors: string[] = [];
    for (const [index, option] of selectQuestion.options.entries()) {
      labels.push(`${option.text}（${this.getSelectAnswerCount(questionnaireAnswers, questionIndex, selectQuestion, index)}人）`);
      data.push(this.getSelectAnswerCount(questionnaireAnswers, questionIndex, selectQuestion, index));
      backgroundColors.push(this.getGraphItemBgColor(index));
    }
    const chartjsData: ChartData = {
      labels: labels,
      datasets: [
        {
          label: "",
          backgroundColor: backgroundColors,
          borderColor: "rgb(200, 200, 200)",
          borderWidth: 1,
          data: data
        }
      ]
    };

    return chartjsData;
  }

  /**
   * ### アンケート回答のテキスト情報を配列で返す
   */
  private createTexts(textQuestion: QuestionnaireTextItem, questionnaireAnswers: Array<QuestionnaireAnswersGetModel>, questionIndex: number): string[] {
    const texts: string[] = [];
    for (const answer of questionnaireAnswers) {
      const question = answer.answers[questionIndex] as QuestionnaireTextItem;
      if (question.answer) {
        texts.push(question.answer);
      }
    }
    return texts;
  }

  /**
   * ### グラフを画面に追加する
   *
   * - DoughnutChart、BarChartは画面作成時にコンポーネントが存在しないと描画が正しく行われない
   * - 画面のDOM要素にDoughnutChartのVueインスタンスを動的に作成して追加する
   * - ただPropを指定しても描画されないのでchartDataChanged()を呼び出して無理やり描画させている
   * - あとで削除できるようにインスタンスは保持しておく
   * - 実行時に既に登録されているインスタンスは削除する
   *   - あわせて作られているinnerHtmlも削除する
   */
  private addGraph(graphQuestion: QuestionAnswerDetail): any {
    // DoughnutChartを追加するDOMを取得
    const element: HTMLElement | null = document.getElementById(`question-${graphQuestion.id}`);
    if (element != null) {
      // 既に子要素がある場合があるので削除する
      element.innerHTML = "";
      switch (graphQuestion.graphType) {
        // DoughnutChartのインスタンスを作成
        case "DoughnutChart": {
          const doughnutChart = Vue.extend(DoughnutChart);
          const instance: any = new doughnutChart({
            propsData: {
              chartData: graphQuestion.displayRateGraph,
              height: 240,
              width: 640
            }
          });
          // インスタンスをマウント
          instance.$mount(element);
          // 無理やり描画を実施
          instance.chartDataChanged();
          return instance;
        }
        // BarChartのインスタンスを作成
        case "BarChart": {
          const barChart = Vue.extend(BarChart);
          // 高さは行数*40pxで動的に指定する
          const height = graphQuestion.rateGraph?.labels ? graphQuestion.rateGraph?.labels.length * 40 : 80;
          const instance: any = new barChart({
            propsData: {
              chartData: graphQuestion.displayRateGraph,
              height: height,
              width: 640
            }
          });
          // インスタンスをマウント
          instance.$mount(element);
          // 無理やり描画を実施
          instance.chartDataChanged();
          return instance;
        }
      }
    }

    return null;
  }

  /**
   * ### アンケート回答の詳細モーダルを表示する
   */
  onQuestionAnswerDetail(graphQuestion: QuestionAnswerDetail): void {
    this.answerDetailModalTitle = graphQuestion.title;
    this.answerDetailModalFields = this.createAnswerDetailModalFields(this.questionnaireAndAnswers.questionnaire);
    this.answerDetailModalItems = this.createAnswerDetailModalItems(graphQuestion);
    this.showAnswerDetailModal = true;
    return;
  }

  /**
   * ### アンケート回答の詳細モーダル列を作成する
   */
  private createAnswerDetailModalFields(questionnaire: QuestionnairesGetModel): Array<QuestionAnswerDetailsTableField> {
    const fields: Array<QuestionAnswerDetailsTableField> = [];

    // 匿名かどうか、更に家族か施設関係者かで表示する列情報が変わる
    if (!questionnaire.hidden) {
      switch (this.requestType) {
        case "families": {
          fields.push({ key: "room", label: "部屋名", sortable: false, sortDirection: "desc", class: "room" });
          fields.push({ key: "name", label: "利用者名", sortable: false, sortDirection: "desc", class: "name" });
          fields.push({ key: "familyName", label: "家族名", sortable: false, sortDirection: "desc", class: "family-name" });
          fields.push({ key: "familyPosition", label: "家族役割", sortable: false, sortDirection: "desc", class: "family-position" });
          fields.push({ key: "familyTel", label: "家族電話番号", sortable: false, sortDirection: "desc", class: "family-tel" });
          break;
        }
        case "otherUserGroups": {
          fields.push({ key: "groupTypeName", label: "グループ種類名", sortable: false, sortDirection: "desc", class: "group-type-name" });
          fields.push({ key: "groupName", label: "グループ名", sortable: false, sortDirection: "desc", class: "group-name" });
          fields.push({ key: "memberName", label: "メンバー名", sortable: false, sortDirection: "desc", class: "member-name" });
          fields.push({ key: "memberTel", label: "メンバー電話番号", sortable: false, sortDirection: "desc", class: "member-tel" });
          fields.push({ key: "memberOrganization", label: "メンバー所属", sortable: false, sortDirection: "desc", class: "member-organization" });
          break;
        }
        default: {
          break;
        }
      }
    }

    // 共通
    fields.push({ key: "answer", label: "アンケート回答", sortable: false, sortDirection: "desc", class: "answer" });
    fields.push({ key: "answerDatetime", label: "回答日時", sortable: false, sortDirection: "desc", class: "answer-datetime" });

    return fields;
  }

  private createAnswerDetailModalItems(graphQuestion: QuestionAnswerDetail): Array<QuestionAnswerDetailsTableItem> {
    const items: Array<QuestionAnswerDetailsTableItem> = [];

    switch (this.requestType) {
      case "families": {
        const families: Array<FamilyGetModel> = this.responsedUsers as Array<FamilyGetModel>;
        const map: Map<string, FamilyGetModel> = this.createFamilyMap(families);
        for (const answer of this.questionnaireAndAnswers.questionnaireAnswers) {
          const item: QuestionAnswerDetailsTableItem = this.createFamilyItem(this.questionnaireAndAnswers.questionnaire, answer, map, graphQuestion);
          items.push(item);
        }
        break;
      }
      case "otherUserGroups": {
        const members: Array<OtherUserGroup> = this.responsedUsers as Array<OtherUserGroup>;
        const map: Map<string, OtherUserGroup> = this.createMemberMap(members);
        for (const answer of this.questionnaireAndAnswers.questionnaireAnswers) {
          const item: QuestionAnswerDetailsTableItem = this.createMemberItem(this.questionnaireAndAnswers.questionnaire, answer, map, graphQuestion);
          items.push(item);
        }
        break;
      }
      default: {
        break;
      }
    }

    return items;
  }

  /**
   * ### 家族用の回答詳細情報を作成する
   */
  private createFamilyItem(
    questionnaire: QuestionnairesGetModel,
    questionnaireAnswer: QuestionnaireAnswersGetModel,
    map: Map<string, FamilyGetModel>,
    graphQuestion: QuestionAnswerDetail
  ): QuestionAnswerDetailsTableItem {
    const item: QuestionAnswerDetailsTableItem = {
      answer: this.createAnswerForQuestionAnswerDetailsTableItem(questionnaire, questionnaireAnswer, graphQuestion),
      answerDatetime: questionnaireAnswer.updated
    };

    if (!questionnaire.hidden) {
      const family: FamilyGetModel | undefined = map.get(questionnaireAnswer.targetSortKey);
      if (family) {
        item.room = family.room;
        item.name = family.name;
        item.familyName = family.familyName;
        item.familyPosition = family.familyPosition;
        item.familyTel = family.familyTel;
      } else {
        // 家族が見つからない場合は家族情報がないので空文字で補完するしかない
        item.room = "";
        item.name = "";
        item.familyName = "";
        item.familyPosition = "";
        item.familyTel = "";
      }
    }

    return item;
  }

  private createAnswerForQuestionAnswerDetailsTableItem(
    questionnaire: QuestionnairesGetModel,
    questionnaireAnswer: QuestionnaireAnswersGetModel,
    graphQuestion: QuestionAnswerDetail
  ): string {
    switch (questionnaireAnswer.answers[graphQuestion.id].type) {
      // 選択肢
      case TemplateType.SELECT: {
        const selectQuestionItem: QuestionnaireSelectItem = questionnaire.questions[graphQuestion.id] as QuestionnaireSelectItem;
        const optionMap: Map<string, string> = new Map();
        for (const option of selectQuestionItem.options) {
          optionMap.set(option.value, option.text);
        }

        // 質問が必須ではない場合、回答がnullがありえるのでチェックする
        const selectAnswerItem: QuestionnaireSelectItem = questionnaireAnswer.answers[graphQuestion.id] as QuestionnaireSelectItem;
        if (selectAnswerItem.answer) {
          const answers: string[] = [];
          for (const answer of selectAnswerItem.answer) {
            const value: string | undefined = optionMap.get(answer);
            if (value) {
              answers.push(value);
            }
          }
          return answers.join(";");
        } else {
          return "";
        }
      }
      // テキスト
      case TemplateType.TEXT: {
        const selectAnswerItem: QuestionnaireTextItem = questionnaireAnswer.answers[graphQuestion.id] as QuestionnaireTextItem;
        return selectAnswerItem.answer ? selectAnswerItem.answer : ""; // 質問が必須ではない場合、回答がnullがありえるのでチェックする
      }
      default: {
        break;
      }
    }
    return "";
  }

  /**
   * ### 施設関係者用の回答詳細情報を作成する
   */
  private createMemberItem(
    questionnaire: QuestionnairesGetModel,
    questionnaireAnswer: QuestionnaireAnswersGetModel,
    map: Map<string, OtherUserGroup>,
    graphQuestion: QuestionAnswerDetail
  ): QuestionAnswerDetailsTableItem {
    const item: QuestionAnswerDetailsTableItem = {
      answer: this.createAnswerForQuestionAnswerDetailsTableItem(questionnaire, questionnaireAnswer, graphQuestion),
      answerDatetime: questionnaireAnswer.updated
    };

    // 施設関係者の情報項目
    if (!questionnaire.hidden) {
      const member: OtherUserGroup | undefined = map.get(questionnaireAnswer.targetSortKey);
      if (member) {
        item.groupTypeName = member.groupTypeName;
        item.groupName = member.name;
        item.memberName = member.memberName;
        item.memberTel = member.memberTel;
        item.memberOrganization = member.memberOrganization;
      } else {
        // 施設関係者が見つからない場合は施設関係者情報がないので空文字で補完するしかない
        item.groupTypeName = "";
        item.groupName = "";
        item.memberName = "";
        item.memberTel = "";
        item.memberOrganization = "";
      }
    }

    return item;
  }

  /**
   * ### アンケート一覧画面へ遷移する
   */
  gotoQuestionnaireList(): void {
    this.$router.push(PcUrl.QuestionnaireList);
    return;
  }
}

export interface QuestionnaireAndAnswers {
  /** アンケート */
  questionnaire: QuestionnairesGetModel;
  /** アンケート回答 */
  questionnaireAnswers: Array<QuestionnaireAnswersGetModel>;
}

/**
 * ### 質問の回答の詳細情報
 *
 * - グラフやテキストで質問の回答を詳細に表示するための情報を保持する
 */
export interface QuestionAnswerDetail {
  /** 一意識別用ID。質問配列の要素番号も兼ねている */
  id: number;
  /** タイトル */
  title: string;

  /** テキスト。グラフではなくテキスト情報を表示するときのみ使用する */
  texts?: string[];

  /** グラフ種別 */
  graphType: "DoughnutChart" | "BarChart" | "None";
  /** 回答グラフ作成で使用するグラフデータ */
  rateGraph?: ChartData;
  /** 表示するグラフデータ */
  displayRateGraph?: ChartData;

  /** モーダルで表示する行情報 */
  items: Array<any>;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

// 親要素からネストしてスタイルを指定する
.questionnaires-detail-tab {
  .box-body {
    .sub-title {
      font-size: 18px;
      font-weight: bold;
    }
    .text-label {
      font-weight: bold;
    }
    .text {
      word-break: break-all;
      font-weight: normal;
    }
    .file-name {
      padding: 6px 12px;
    }
    .preview-btn {
      width: 120px;
      min-width: 120px;
      max-width: 120px;
    }
    .buttons {
      width: 780px;
    }

    .response-rate {
      width: 780px;
    }

    .questions {
      // width: 100%;
      max-width: 780px;

      .title {
        font-weight: bold;
      }

      .question {
        width: 100%;
        border-top: 1px solid $border-lightgray;
        padding: 12px 0;

        .text-question {
          .count-area {
            width: 120px;
          }
          .text-area {
            width: calc(780px - 120px);
            max-width: calc(780px - 120px);
            min-width: calc(780px - 120px);
            .text {
              // 文字数が長い場合は省略記号で表示する
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
          }
          .count-title,
          .text-title {
            font-weight: bold;
            font-size: 16px;
            color: $font-disable-font-color;
          }
        }
      }
    }
  }
}
</style>
